import React from 'react'
import { createRoot } from 'react-dom/client'
import { Notification } from '@douyinfe/semi-ui'
import { IconAlertTriangle } from '@douyinfe/semi-icons'
// 全局样式重置（reset-css）：本地打包，避免 unpkg CDN 在大陆访问超时
import 'reset-css'
import './index.css'
import App from './App'
import { reportFromWindowError, reportFromRejection } from './utils/errorReporter'

/**
 * 全局兜底：捕获不在 React 错误边界范围内的同步错误 / 异步 Promise 拒绝
 * - ErrorBoundary 抓不到：异步回调、Promise 拒绝、window-level 错误
 * - 这里上报到 errorReporter，**并且给用户弹一次提示**（按错误指纹节流，避免重复轰炸）
 */

interface ErrorEventLike {
	message?: string
	reason?: unknown
	error?: Error
	filename?: string
	lineno?: number
	colno?: number
}

/**
 * ResizeObserver loop completed with undelivered notifications.
 * 是浏览器自带的良性警告：每次 ResizeObserver 回调里改了 DOM 触发 layout，
 * 浏览器就会打一次，**和业务错误无关**。直接过滤掉，不上报、不弹通知。
 */
const isBenignResizeObserverWarning = (msg: string | undefined): boolean => {
	if (!msg) return false
	return msg.includes('ResizeObserver loop completed with undelivered notifications')
}

/** 错误指纹：相同 file:line:message 视为同一次，只弹一次 */
const errorFingerprints = new Map<string, number>()
const FINGERPRINT_GAP = 3000

const shouldShowToast = (fingerprint: string): boolean => {
	const last = errorFingerprints.get(fingerprint) || 0
	const now = Date.now()
	if (now - last < FINGERPRINT_GAP) return false
	errorFingerprints.set(fingerprint, now)
	return true
}

/** 从 Error.stack 拿首行（"at func (file:line:col)"），作为定位信息 */
const firstStackLocation = (err: Error | undefined): string | null => {
	if (!err?.stack) return null
	const lines = err.stack
		.split('\n')
		.map((l) => l.trim())
		.filter(Boolean)
	// 跳过第一行 "Error: message"，从第二行开始
	for (let i = 1; i < lines.length; i++) {
		const m = lines[i].match(/\((.+?):(\d+):(\d+)\)/)
		if (m) return `${m[1].split('/').pop()}:${m[2]}:${m[3]}`
	}
	return null
}

const showToast = (title: string, primary: string, location: string | null): void => {
	Notification.error({
		title,
		content: (
			<div>
				<div style={{ wordBreak: 'break-word' }}>{primary}</div>
				{location && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>at {location}</div>}
			</div>
		),
		duration: 6,
		icon: <IconAlertTriangle />
	})
}

window.addEventListener('error', (event: ErrorEvent) => {
	const msg = event.message || event.error?.message || '未捕获的运行时错误'
	if (isBenignResizeObserverWarning(msg)) return // 良性警告静默
	reportFromWindowError(event)
	const err = event.error instanceof Error ? event.error : undefined
	const location = event.filename
		? `${event.filename.split('/').pop()}:${event.lineno}:${event.colno}`
		: firstStackLocation(err)
	const fp = `${msg}|${location || ''}`
	if (shouldShowToast(fp)) showToast('运行时错误', msg, location)
})

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
	const reason = event.reason
	const err = reason instanceof Error ? reason : new Error(String(reason))
	const msg = err.message || String(reason)
	if (isBenignResizeObserverWarning(msg)) return
	reportFromRejection(reason)
	const location = firstStackLocation(err)
	const fp = `${msg}|${location || ''}`
	if (shouldShowToast(fp)) showToast('未捕获的 Promise 错误', msg, location)
})

const rootElement = document.getElementById('root')
if (rootElement) {
	const root = createRoot(rootElement)
	root.render(<App />)
}
